import { useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser } from "@/store/slices/user.slice";
import type { IWorkflowInstance } from "@/types/(company)/workflow.interface";

/**
 * Derives workflow approver state from a workflow instance.
 *
 * Centralises the repeated pattern:
 *   const isCurrentApprover = Number(user.id) === Number(instance?.currentApprover?.id)
 *   const hasWorkflow = !!instance
 *   const isMyTurn = isPending && hasWorkflow && isCurrentApprover
 *
 * @param instance  The workflow instance from useWorkflowInstance
 * @param status    The request status string (e.g. "PENDING", "pending")
 */
export function useApproverContext(
  instance: IWorkflowInstance | null | undefined,
  status: string | null | undefined,
) {
  const { user: currentUser } = useAppSelector(selectUser);
  const currentUserId = currentUser?.id ?? null;

  const hasWorkflow = !!instance;
  const currentApproverId = instance?.currentApprover?.id ?? null;
  const isPending =
    status != null &&
    (status.toLowerCase() === "pending" || status.toUpperCase() === "PENDING");

  const isSingleApprover =
    currentUserId != null &&
    currentApproverId != null &&
    Number(currentUserId) === Number(currentApproverId);

  // Parallel-approval gate: the user acts if they are one of the still-pending
  // active approvers (currentApprover is null on parallel nodes).
  const isPendingParallelApprover =
    currentUserId != null &&
    (instance?.activeApprovers ?? []).some(
      (a) =>
        Number(a.userId) === Number(currentUserId) &&
        a.status?.toLowerCase() === "pending",
    );

  const isCurrentApprover = isSingleApprover || isPendingParallelApprover;

  const isMyTurn = isPending && hasWorkflow && isCurrentApprover;

  return {
    hasWorkflow,
    isPending,
    isMyTurn,
    isCurrentApprover,
    currentUserId,
    currentApproverId,
    instanceId: instance?.id ?? null,
  } as const;
}
